import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { type FC, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import Loader from 'components/v2/common/loader';
import ServiceDefaultConsultantAssignments from 'views/v2/adminPanel/components/serviceDefaultConsultantAssignments';

import api from 'api';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type {
    ServiceConsultantProgram,
    ServiceConsultantsResponse,
    ServiceProviderAdminRow,
} from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface ServiceProviderDefaultConsultantsDialogProps {
    provider: ServiceProviderAdminRow;
    onClose: () => void;
}

const DialogHeading: FC<{ provider: ServiceProviderAdminRow }> = ({ provider }) => {
    const { translate } = useTranslate();

    return (
        <DialogTitle>
            <Typography variant="h6" component="span" sx={{ display: 'block', fontWeight: 700 }}>
                {translate('manageDefaultConsultants')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {provider.company_name}
            </Typography>
        </DialogTitle>
    );
};

export const ServiceProviderDefaultConsultantsDialog: FC<
    ServiceProviderDefaultConsultantsDialogProps
> = ({ provider, onClose }) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const onCloseRef = useRef(onClose);

    const [data, setData] = useState<ServiceConsultantsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [updatingServiceIds, setUpdatingServiceIds] = useState<Set<number>>(new Set());

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            try {
                const response = await api.serviceProvider.getServiceConsultants(provider.id);
                if (!cancelled) setData(response.data);
            } catch (error) {
                if (cancelled) return;
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('somethingWentWrong')),
                        type: 'error',
                    })
                );
                onCloseRef.current();
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [dispatch, provider.id, translate]);

    const setServiceUpdating = (serviceId: number, isUpdating: boolean) => {
        setUpdatingServiceIds((current) => {
            const next = new Set(current);
            if (isUpdating) next.add(serviceId);
            else next.delete(serviceId);
            return next;
        });
    };

    const handleAssignmentChange = async (
        service: ServiceConsultantProgram,
        consultantId: number | null
    ) => {
        if (consultantId === service.consultant_id || updatingServiceIds.has(service.service_id)) {
            return;
        }

        setServiceUpdating(service.service_id, true);

        try {
            const response =
                consultantId === null
                    ? await api.serviceProvider.removeServiceConsultant(
                          provider.id,
                          service.service_id
                      )
                    : await api.serviceProvider.setServiceConsultant(
                          provider.id,
                          service.service_id,
                          { consultant_id: consultantId }
                      );

            setData((current) =>
                current
                    ? {
                          ...current,
                          services: current.services.map((currentService) =>
                              currentService.service_id === service.service_id
                                  ? response.data
                                  : currentService
                          ),
                      }
                    : current
            );
            dispatch(
                setAlert({
                    message: translate('defaultConsultantUpdated'),
                    type: 'success',
                })
            );
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        } finally {
            setServiceUpdating(service.service_id, false);
        }
    };

    const dialogPaperSx = {
        borderRadius: fullScreen ? 0 : 2,
        maxHeight: fullScreen ? '100%' : '90vh',
    };

    if (isLoading || !data) {
        return (
            <Dialog
                open
                onClose={onClose}
                fullWidth
                maxWidth="md"
                fullScreen={fullScreen}
                scroll="paper"
                slotProps={{ paper: { sx: dialogPaperSx } }}
            >
                <DialogHeading provider={provider} />
                <DialogContent dividers>
                    <Loader
                        isCircular
                        message={translate('loadingDefaultConsultants')}
                        rootSx={{ minHeight: 320 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button variant="outlined" onClick={onClose}>
                        {translate('close')}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    const isUpdating = updatingServiceIds.size > 0;

    return (
        <Dialog
            open
            onClose={isUpdating ? undefined : onClose}
            fullWidth
            maxWidth="md"
            fullScreen={fullScreen}
            scroll="paper"
            slotProps={{ paper: { sx: dialogPaperSx } }}
        >
            <DialogHeading provider={provider} />
            <ServiceDefaultConsultantAssignments
                defaultConsultant={data.default_consultant}
                services={data.services}
                updatingServiceIds={updatingServiceIds}
                onAssignmentChange={handleAssignmentChange}
            />
            <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Button variant="outlined" onClick={onClose} disabled={isUpdating}>
                    {translate('close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ServiceProviderDefaultConsultantsDialog;
